<div class="pdf-builder-page">
  <header class="builder-header">
    <div>
      <h1>Document templates</h1>
      <p class="header-copy">Create, version, publish, archive, and roll back HTML templates for document output.</p>
    </div>
  </header>

  <div class="builder-workspace" [class.loading]="loadingInitialData">
    <aside class="template-library">
      <div class="panel-heading">
        <div>
          <span class="panel-label">Library</span>
          <strong>{{ templates.length }}</strong>
        </div>
        <div class="panel-heading-actions">
          <i *ngIf="loadingLibrary" class="fa-regular fa-spinner fa-spin"></i>
          <button type="button" class="builder-btn builder-btn-primary builder-btn-sm" (click)="startNewTemplate()">
            <span class="button-plus" aria-hidden="true"></span>
            <span>New template</span>
          </button>
        </div>
      </div>

      <div class="library-filters">
        <label>
          <span>Search</span>
          <input
            type="search"
            class="form-control"
            [(ngModel)]="searchTerm"
            placeholder="Template name or type"
          />
        </label>

        <label>
          <span>Document type</span>
          <select class="form-control" [(ngModel)]="documentTypeFilter" (ngModelChange)="onFilterChange()">
            <option value="">All types</option>
            <option *ngFor="let documentType of documentTypes; trackBy: trackByDocumentType" [value]="documentType.key">
              {{ documentType.displayName }}
            </option>
          </select>
        </label>

        <label>
          <span>Status</span>
          <select class="form-control" [(ngModel)]="statusFilter" (ngModelChange)="onFilterChange()">
            <option value="">All statuses</option>
            <option *ngFor="let status of statusOptions" [value]="status">{{ status }}</option>
          </select>
        </label>
      </div>

      <div class="template-list" *ngIf="visibleTemplates.length > 0; else emptyLibrary">
        <button
          type="button"
          class="template-list-item"
          *ngFor="let template of visibleTemplates; trackBy: trackByTemplateId"
          [class.active]="selectedTemplate?.id === template.id"
          (click)="openTemplate(template)"
        >
          <span class="template-item-main">
            <strong>{{ template.name }}</strong>
            <small>{{ getTemplateSubtitle(template) }}</small>
          </span>
          <span class="status-pill" [class.draft]="template.status === 'DRAFT'" [class.published]="template.status === 'PUBLISHED'" [class.archived]="template.status === 'ARCHIVED'">
            {{ template.status }}
          </span>
        </button>
      </div>

      <ng-template #emptyLibrary>
        <div class="empty-state compact">
          <i class="fa-regular fa-file-lines"></i>
          <span>No templates found</span>
        </div>
      </ng-template>
    </aside>

    <main class="template-editor" [class.empty]="mode === 'idle'">
      <div *ngIf="loadingTemplate" class="loading-overlay">
        <i class="fa-regular fa-spinner fa-spin"></i>
      </div>

      <ng-container *ngIf="mode !== 'idle'; else emptyWorkspace">
      <div class="editor-toolbar">
        <div>
          <span class="panel-label">{{ mode === 'new' ? 'New draft' : 'Selected version' }}</span>
          <h2>{{ templateForm.name || 'Untitled template' }}</h2>
        </div>

        <div class="toolbar-actions">
          <span *ngIf="selectedTemplate" class="status-pill large" [class.draft]="selectedTemplate.status === 'DRAFT'" [class.published]="selectedTemplate.status === 'PUBLISHED'" [class.archived]="selectedTemplate.status === 'ARCHIVED'">
            {{ selectedTemplate.status }}
          </span>

          <button type="button" class="builder-btn builder-btn-secondary" *ngIf="canCreateNewVersion" (click)="createNewVersion()">
            <i class="fa-regular fa-code-branch"></i>
            <span>Create new version</span>
          </button>

          <button type="button" class="builder-btn builder-btn-secondary" *ngIf="canRollbackVersion(selectedTemplate)" (click)="rollbackTemplate()">
            <i class="fa-regular fa-rotate-left"></i>
            <span>Rollback</span>
          </button>

          <button type="button" class="builder-btn builder-btn-secondary" *ngIf="canArchive" (click)="archiveTemplate()">
            <i class="fa-regular fa-box-archive"></i>
            <span>Archive</span>
          </button>

          <button type="button" class="builder-btn builder-btn-secondary" *ngIf="canPublish" (click)="publishTemplate()">
            <i class="fa-regular fa-paper-plane"></i>
            <span>Publish</span>
          </button>

          <button type="button" class="builder-btn builder-btn-primary" [disabled]="!canSave" (click)="saveTemplate()">
            <i class="fa-regular" [class.fa-spinner]="saving" [class.fa-spin]="saving" [class.fa-floppy-disk]="!saving"></i>
            <span>{{ mode === 'new' ? 'Create' : 'Save' }}</span>
          </button>
        </div>
      </div>

      <section class="form-grid">
        <label>
          <span>Document type</span>
          <select
            class="form-control"
            [(ngModel)]="templateForm.documentTypeId"
            [disabled]="!canEdit"
          >
            <option value="">Select type</option>
            <option *ngFor="let documentType of documentTypes; trackBy: trackByDocumentType" [value]="documentType.key">
              {{ documentType.displayName }}
            </option>
          </select>
        </label>

        <label>
          <span>Name</span>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="templateForm.name"
            [disabled]="!canEdit"
            placeholder="Product catalogue"
          />
        </label>

        <label class="full-width">
          <span>Description</span>
          <textarea
            class="form-control"
            rows="2"
            [(ngModel)]="templateForm.description"
            [disabled]="!canEdit"
            placeholder="Internal notes about this template"
          ></textarea>
        </label>
      </section>

      <section *ngIf="selectedDocumentType" class="type-context">
        <div>
          <strong>{{ selectedDocumentType.displayName }}</strong>
          <p>{{ selectedDocumentType.description }}</p>
        </div>
        <div>
          <span>Variables</span>
          <p>{{ selectedDocumentType.variableDocumentation }}</p>
        </div>
      </section>

      <section class="chat-authoring">
        <div class="chat-panel-heading">
          <div>
            <span class="panel-label">Generator</span>
            <h3>Catalogue conversation</h3>
          </div>
          <span *ngIf="generatedHtmlPendingSave" class="pending-save-pill">Pending save</span>
        </div>

        <div class="chat-messages" *ngIf="chatMessages.length > 0 || generationStatusText; else emptyChat">
          <article
            class="chat-message"
            *ngFor="let message of chatMessages; trackBy: trackByChatMessageId"
            [class.user]="message.role === 'user'"
            [class.assistant]="message.role === 'assistant'"
            [class.error]="message.kind === 'error'"
            [class.result]="message.kind === 'result'"
            [class.question]="message.kind === 'question'"
          >
            <div class="chat-avatar">
              <i
                class="fa-regular"
                [class.fa-user]="message.role === 'user'"
                [class.fa-sparkles]="message.role === 'assistant' && message.kind !== 'error'"
                [class.fa-triangle-exclamation]="message.kind === 'error'"
              ></i>
            </div>
            <div class="chat-bubble">
              <p>{{ message.text }}</p>
              <small *ngIf="message.detail">{{ message.detail }}</small>
            </div>
          </article>

          <article
            *ngIf="generationStatusText"
            class="chat-message assistant pending"
            role="status"
            aria-live="polite"
          >
            <div class="chat-avatar">
              <i class="fa-regular fa-spinner fa-spin"></i>
            </div>
            <div class="chat-bubble">
              <p>{{ generationStatusText }}</p>
            </div>
          </article>
        </div>

        <ng-template #emptyChat>
          <div class="empty-state chat-empty">
            <i
              class="fa-regular"
              [class.fa-spinner]="loadingChatHistory"
              [class.fa-spin]="loadingChatHistory"
              [class.fa-messages]="!loadingChatHistory"
            ></i>
            <span>{{ loadingChatHistory ? 'Loading conversation history' : 'Start from the current template context' }}</span>
          </div>
        </ng-template>

        <div *ngIf="chatUnavailableMessage" class="chat-unavailable">
          {{ chatUnavailableMessage }}
        </div>

        <div class="chat-composer">
          <div class="chat-composer-input">
            <textarea
              class="chat-input"
              [(ngModel)]="chatDraft"
              [disabled]="!canEdit || sendingMessage || creatingDraftForChat"
              (keydown)="onChatComposerKeydown($event)"
              rows="3"
              placeholder="Generate a catalogue grouped by category with product images and prices."
            ></textarea>
            <div *ngIf="pendingAttachments.length > 0" class="chat-attachments">
              <span
                class="chat-attachment-chip"
                *ngFor="let attachment of pendingAttachments; trackBy: trackByAttachmentId"
                [class.uploading]="attachment.status === 'uploading'"
                [class.error]="attachment.status === 'error'"
                [title]="attachment.error || attachment.filename"
              >
                <i class="fa-regular" [ngClass]="attachmentIcon(attachment)"></i>
                <span class="chip-name">{{ attachment.filename }}</span>
                <button
                  type="button"
                  class="chip-remove"
                  (click)="removeAttachment(attachment.localId)"
                  [attr.aria-label]="'Remove ' + attachment.filename"
                >
                  <i class="fa-regular fa-xmark"></i>
                </button>
              </span>
            </div>
          </div>
          <div class="chat-composer-actions">
            <input
              #attachmentInput
              type="file"
              multiple
              [accept]="attachmentAccept"
              (change)="onAttachmentsSelected($event)"
              style="display: none"
            />
            <button
              type="button"
              class="builder-btn builder-btn-secondary chat-attach-btn"
              [disabled]="!canPickAttachment"
              (click)="triggerAttachmentPicker()"
              [attr.aria-label]="'Attach files'"
              title="Attach reference files (images, PDF, markdown)"
            >
              <i class="fa-regular fa-paperclip"></i>
            </button>
            <button type="button" class="builder-btn builder-btn-primary" [disabled]="!canSendChatMessage" (click)="sendChatMessage()">
              <i
                class="fa-regular"
                [class.fa-paper-plane]="!sendingMessage && !creatingDraftForChat"
                [class.fa-spinner]="sendingMessage || creatingDraftForChat"
                [class.fa-spin]="sendingMessage || creatingDraftForChat"
              ></i>
              <span>{{ sendingMessage || creatingDraftForChat ? 'Generating' : 'Send' }}</span>
            </button>
          </div>
        </div>
      </section>

      <section class="source-panel">
        <button type="button" class="source-toggle" (click)="sourceExpanded = !sourceExpanded" [attr.aria-expanded]="sourceExpanded">
          <span>
            <i class="fa-regular fa-code"></i>
            HTML source
          </span>
          <i class="fa-regular" [class.fa-chevron-down]="!sourceExpanded" [class.fa-chevron-up]="sourceExpanded"></i>
        </button>

        <div class="code-grid" *ngIf="sourceExpanded">
          <label class="full-width">
            <span>HTML template</span>
            <textarea
              class="code-editor"
              spellcheck="false"
              [(ngModel)]="templateForm.htmlTemplate"
              [disabled]="!canEdit"
              placeholder="<html><head><style>...</style></head><body>...</body></html>"
            ></textarea>
          </label>
        </div>
      </section>

      <section class="group-settings" *ngIf="mode === 'edit' && selectedTemplate?.templateGroupId">
        <button
          type="button"
          class="source-toggle"
          (click)="groupSettingsExpanded = !groupSettingsExpanded"
          [attr.aria-expanded]="groupSettingsExpanded"
        >
          <span>
            <i class="fa-regular fa-gear"></i>
            Settings
          </span>
          <i class="fa-regular" [class.fa-chevron-down]="!groupSettingsExpanded" [class.fa-chevron-up]="groupSettingsExpanded"></i>
        </button>

        <div class="group-settings-body" *ngIf="groupSettingsExpanded">
          <p class="group-settings-intro">
            These settings apply to all versions of this template and control how it is presented to end users.
          </p>

          <fieldset class="group-settings-fieldset">
            <legend>Display name</legend>
            <p class="group-settings-hint">Shown in the export menu. The end user sees the entry for their current language; falls back to English, then the internal template name.</p>
            <div class="group-settings-locale-grid">
              <label *ngFor="let locale of availableLocales">
                <span>{{ locale.short }} <small>({{ locale.language }})</small></span>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="groupSettingsForm.displayName[locale.short]"
                  placeholder="Product sheet"
                />
              </label>
              <label *ngIf="availableLocales.length === 0">
                <span>en</span>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="groupSettingsForm.displayName['en']"
                  placeholder="Product sheet"
                />
              </label>
            </div>
          </fieldset>

          <fieldset class="group-settings-fieldset">
            <legend>File name pattern</legend>
            <input
              type="text"
              class="form-control"
              [(ngModel)]="groupSettingsForm.fileNamePattern"
              placeholder="product-sheet-{date}-{entityIds}.pdf"
            />
            <p class="group-settings-hint">
              Use <code>&#123;date&#125;</code>, <code>&#123;documentTypeId&#125;</code>, <code>&#123;entityIds&#125;</code>, or <code>&#123;params.&lt;key&gt;&#125;</code>. <code>.pdf</code> is appended automatically; unsafe characters are sanitized.
            </p>
          </fieldset>

          <fieldset class="group-settings-fieldset">
            <legend>Visibility</legend>
            <p class="group-settings-hint">
              Leave a list empty to apply no restriction on that dimension. The template is visible to a user only when they match every non-empty list — admins included.
            </p>

            <div class="group-settings-grid">
              <div class="group-settings-list">
                <span class="group-settings-list-title">Roles</span>
                <lib-gung-input-multi-select
                  [suppressLabel]="true"
                  [optionsList]="roleOptions"
                  (selectedValues)="onAllowedRolesChanged($event)"
                  [ngBootstrapContainer]="'body'"
                  [showId]="false"
                  [previewField]="'name'"
                />
              </div>

              <div class="group-settings-list">
                <span class="group-settings-list-title">Groups</span>
                <lib-gung-input-multi-select
                  [suppressLabel]="true"
                  [optionsList]="userGroupOptions"
                  (selectedValues)="onAllowedUserGroupsChanged($event)"
                  [ngBootstrapContainer]="'body'"
                  [showId]="false"
                  [previewField]="'name'"
                />
              </div>

              <div class="group-settings-list">
                <span class="group-settings-list-title">Flows</span>
                <lib-gung-input-multi-select
                  [suppressLabel]="true"
                  [optionsList]="flowOptions"
                  (selectedValues)="onAllowedFlowsChanged($event)"
                  [ngBootstrapContainer]="'body'"
                  [showId]="false"
                  [previewField]="'name'"
                />
              </div>
            </div>

            <div *ngIf="groupSettingsHidden" class="group-settings-warning" role="alert">
              <i class="fa-regular fa-triangle-exclamation"></i>
              This template is hidden from everyone — including admins — until at least one role, group, or flow is selected.
            </div>
          </fieldset>

          <div class="group-settings-actions">
            <button
              type="button"
              class="builder-btn builder-btn-primary"
              [disabled]="!canSaveGroupSettings"
              (click)="saveGroupSettings()"
            >
              <i
                class="fa-regular"
                [class.fa-spinner]="savingGroupSettings"
                [class.fa-spin]="savingGroupSettings"
                [class.fa-floppy-disk]="!savingGroupSettings"
              ></i>
              <span>Save settings</span>
            </button>
          </div>
        </div>
      </section>

      <div class="danger-actions" *ngIf="selectedTemplate">
        <span class="danger-zone-label">Danger zone</span>
        <button type="button" class="builder-btn builder-btn-danger builder-btn-sm" (click)="deleteTemplateVersion()">
          <i class="fa-regular fa-trash-can"></i>
          <span>Delete version</span>
        </button>
        <button type="button" class="builder-btn builder-btn-danger builder-btn-sm" (click)="deleteTemplateGroup()">
          <i class="fa-regular fa-trash-can-list"></i>
          <span>Delete template</span>
        </button>
      </div>
      </ng-container>

      <ng-template #emptyWorkspace>
        <section class="workspace-empty">
          <i class="fa-regular fa-file-lines"></i>
          <h2>Select a template</h2>
          <p>Choose an existing template from the library, or create a new template when you are ready.</p>
          <button type="button" class="builder-btn builder-btn-primary" (click)="startNewTemplate()">
            <span class="button-plus" aria-hidden="true"></span>
            <span>New template</span>
          </button>
        </section>
      </ng-template>
    </main>

    <aside class="template-sidepanel">
      <section class="pdf-preview-panel">
        <div class="panel-heading">
          <div>
            <span class="panel-label">Preview</span>
            <strong>Generated PDF</strong>
          </div>
          <a
            *ngIf="generatedPdfUrl"
            class="builder-btn builder-btn-secondary builder-btn-xs"
            [href]="generatedPdfUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="fa-regular fa-arrow-up-right-from-square"></i>
            <span>Open</span>
          </a>
        </div>

        <div class="pdf-preview-frame" *ngIf="hasGeneratedPreview; else emptyPdfPreview">
          <embed [src]="generatedPdfSafeUrl" width="100%" height="100%" type="application/pdf" />
        </div>

        <ng-template #emptyPdfPreview>
          <div class="empty-state pdf-empty">
            <i *ngIf="!pdfPreviewError" class="fa-regular fa-file-pdf"></i>
            <i *ngIf="pdfPreviewError" class="fa-regular fa-triangle-exclamation"></i>
            <span>{{ pdfPreviewError || 'No PDF rendered yet' }}</span>
          </div>
        </ng-template>
      </section>

      <section class="versions-panel">
        <div class="panel-heading">
          <div>
            <span class="panel-label">Versions</span>
            <strong>{{ versionHistory.length }}</strong>
          </div>
          <i class="fa-regular fa-clock-rotate-left"></i>
        </div>

        <div class="version-list version-timeline" *ngIf="versionHistory.length > 0; else emptyVersions">
          <div
            class="version-item"
            *ngFor="let version of versionHistory; trackBy: trackByTemplateId"
            [class.active]="selectedTemplate?.id === version.id"
            [class.rollback-available]="canRollbackVersion(version)"
          >
            <button type="button" class="version-main" (click)="openTemplate(version)">
              <span class="version-node"></span>
              <span class="version-copy">
                <span class="version-title-row">
                  <strong>Version {{ version.version }}</strong>
                  <span class="status-pill small" [class.draft]="version.status === 'DRAFT'" [class.published]="version.status === 'PUBLISHED'" [class.archived]="version.status === 'ARCHIVED'">
                    {{ version.status }}
                  </span>
                </span>
                <span class="version-meta">
                  {{ getVersionCreatedAge(version) }} by {{ getVersionCreatedBy(version) }}
                </span>
              </span>
            </button>

            <button
              *ngIf="canRollbackVersion(version)"
              type="button"
              class="builder-btn builder-btn-secondary builder-btn-xs version-rollback"
              (click)="rollbackTemplate(version)"
            >
              <i class="fa-regular fa-rotate-left"></i>
              <span>Rollback</span>
            </button>
          </div>
        </div>

        <ng-template #emptyVersions>
          <div class="empty-state compact">
            <span>No versions yet</span>
          </div>
        </ng-template>
      </section>
    </aside>
  </div>
</div>
